import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Attribute, AttributeGrid, BackButton, Badge, Button, Modal, PageHeader, useConfirm,
} from '@shared-ui/components';
import { pageLinkOf } from '@padcanvas/shared';
import { publishedLinksApi, type PublishedLink } from '../api/publishedLinksApi';
import { LinkForm, QrPanel } from './PublishedLinkForm';
import {
  destinationLabel, destinationTarget, fmtLoad, loadRate, publicUrl, publisherLabel, shortUrl,
} from './publishedLinkFormat';
import LinkCampaigns from './LinkCampaigns';
import LinkActivity from './site-usage/science/LinkActivity';
import './PublishedLinks.css';

/**
 * One published link — what it is, where it goes, how often it is opened, and
 * the things done to it: edit (repoint, re-tag), retire / restore, and its QR.
 *
 * Read from the list query rather than a by-slug endpoint: the list is small,
 * already cached from the page that led here, and carries the opens and the
 * set's title that a single-link read would not.
 */

const fmtWhen = (iso?: string) => (iso ? new Date(iso).toLocaleString() : '—');

export default function PublishedLinkDetail() {
  const { slug = '' } = useParams();
  const navigate = useNavigate();
  // The activity box reads a fixed month; the page has no window picker of its own.
  const [[activityFrom, activityTo]] = useState(() => [new Date(Date.now() - 30 * 864e5), new Date()] as const);
  const queryClient = useQueryClient();
  const { confirm, dialog } = useConfirm();
  const [editing, setEditing] = useState(false);

  const { data, isLoading, error } = useQuery({ queryKey: ['publishedLinks'], queryFn: publishedLinksApi.list });
  const link = data?.links.find(l => l.slug === slug);

  const save = useMutation({
    mutationFn: (changes: Partial<PublishedLink>) => publishedLinksApi.update(slug, changes),
    onSuccess: () => { void queryClient.invalidateQueries({ queryKey: ['publishedLinks'] }); setEditing(false); },
  });

  const back = <BackButton to="/published-links" label="Published Links" />;
  if (isLoading) return <div className="pl">{back}<p className="pl-sub">Loading…</p></div>;
  if (error) return <div className="pl">{back}<p className="pl-error">Error: {(error as Error).message}</p></div>;
  if (!link) return <div className="pl">{back}<p className="pl-sub">No published link /p/{slug}.</p></div>;

  const url = publicUrl(link);
  const active = link.status === 'active';
  const d = link.destination;

  /** Retiring breaks a URL that is already printed and posted, so it asks.
   *  Restoring puts one back, which needs no second thought. */
  const toggleStatus = async () => {
    if (active && !(await confirm({
      message: (
        <>
          Retire <strong>{shortUrl(url)}</strong>? It stops sending people to its destination
          and stops counting them. It can be restored later; the URL stays reserved.
        </>
      ),
      confirmLabel: 'Retire',
      confirmVariant: 'danger',
    }))) return;
    save.mutate({ status: active ? 'retired' : 'active' });
  };

  return (
    <div className="pl">
      {back}

      <PageHeader
        title={<span className="pl-slug-title">{shortUrl(url)}</span>}
        subtitle={
          <>
            {destinationLabel(d)}
            {link.destinationTitle && <> · {link.destinationTitle}</>}
          </>
        }
        actions={
          <>
            {/* The Lifecycle funnel for the people this link brought — its
                criteria built from the link, not a stored segment. */}
            <Button variant="secondary" size="sm"
              onClick={() => navigate(`/dashboard?tab=lifecycle&link=${encodeURIComponent(link.slug)}`)}>
              Open funnel for this link
            </Button>
            <Button variant="secondary" size="sm" onClick={toggleStatus} disabled={save.isPending}>
              {active ? 'Retire' : 'Restore'}
            </Button>
            <Button variant="primary" size="sm" onClick={() => { save.reset(); setEditing(true); }}>
              Edit
            </Button>
          </>
        }
      />

      {save.error && !editing && <p className="pl-error">{(save.error as Error).message}</p>}

      <div className="pl-detail">
        <AttributeGrid>
          <Attribute label="Status">
            <span className="sui-attr-value">
              <Badge variant={active ? 'success' : 'default'} size="sm">{link.status}</Badge>
            </span>
          </Attribute>
          <Attribute label={`Opens · last ${data?.opensWindowDays ?? 30} days`}>
            {(link.opens ?? 0).toLocaleString()}
          </Attribute>
          <Attribute label="Loads · share of opens">
            {`${(link.loads ?? 0).toLocaleString()} · ${loadRate(link)}`}
          </Attribute>
          <Attribute label="Load time · P50 / P70 / P90">
            {`${fmtLoad(link.p50LoadMs)} / ${fmtLoad(link.p70LoadMs)} / ${fmtLoad(link.p90LoadMs)}`}
          </Attribute>

          <Attribute label="URL" span>
            <span className="sui-attr-value">
              <a href={url} target="_blank" rel="noreferrer" className="pl-slug">{url}</a>
              {pageLinkOf(link) && <span className="pl-sub">also {shortUrl(url).split('/')[0]}/p/{link.slug}</span>}
            </span>
          </Attribute>

          <Attribute label="Points at">{destinationLabel(d)}</Attribute>
          <Attribute label={d.kind === 'site' ? 'Page' : d.kind === 'route' ? 'Path' : 'Target'} mono>
            {destinationTarget(d)}
          </Attribute>
          {d.kind === 'chapter-practice' && d.videoUrl && (
            <Attribute label="Video" span>
              <span className="sui-attr-value">
                <a href={d.videoUrl} target="_blank" rel="noreferrer">{d.videoUrl}</a>
              </span>
            </Attribute>
          )}

          <Attribute label="Publisher">{publisherLabel(link)}</Attribute>
          <Attribute label="Campaign">
            {link.campaign ? `${link.campaign}${link.variant ? ` · ${link.variant}` : ''}` : '—'}
          </Attribute>

          {link.notes && <Attribute label="Notes" span>{link.notes}</Attribute>}

          <Attribute label="Created">{`${fmtWhen(link.createdAt)} · ${link.createdBy}`}</Attribute>
          <Attribute label="Updated">
            {link.updatedAt ? `${fmtWhen(link.updatedAt)}${link.updatedBy ? ` · ${link.updatedBy}` : ''}` : '—'}
          </Attribute>
        </AttributeGrid>

        <section className="pl-qr-card">
          <h2>QR code</h2>
          <QrPanel link={link} />
        </section>
      </div>

      <LinkCampaigns slug={link.slug} />
      <LinkActivity slug={link.slug} from={activityFrom} to={activityTo} />

      <Modal isOpen={editing} onClose={() => setEditing(false)} title={`Edit ${shortUrl(url)}`} size="md">
        {editing && (
          <LinkForm
            link={link}
            saving={save.isPending}
            error={save.error as Error | null}
            onCancel={() => setEditing(false)}
            onCreate={() => undefined}
            onSave={(_slug, changes) => save.mutate(changes)}
          />
        )}
      </Modal>

      {dialog}
    </div>
  );
}
